import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';

import { useMapInteractionStore, useMapStateStore } from '../../../stores';
import { getLatLngFromFocusXYZ, animateMapNavigation } from '../GoogleMap/GoogleMapHelpers';
import { clusterOverlappingLabels } from '../MapPlace/MapPlaceLabelClusterHelpers';
import { getCenterFromPlace } from '../MapLabels/MapLabelsHelpers';
import {
  getZoomScale,
  pickPrimaryLevelOfCareUuid,
  pickZoomZoneTargetLevelSvgId,
} from './MapOverlayHelpers';
import MapLabels from '../MapLabels/MapLabels';

const ZOOM_ZONE_LEVEL_SWITCH_DELAY = 600;

const MapOverlayLabels = ( {
  places,
  units,
  unitsVersion,
  isBirdsEye,
  floorZoomZones,
  defaultLevelOfCare,
  parsedViewBox,
  clusterViewBox,
  containerSize,
  activeZoom,
  maxZoom,
  effectiveMaxZoom,
  mapZoomToEnableLevels,
  zoomZonesActivationLevel,
  platformClusterPlaceLabels,
  platformShowOccupancy,
  platformShowMapPlaceLabelsInitially,
  platformShowLowerValuePlaceLabelsInitially,
  platformShowZoomZonesOnMapLevels,
  markersVisible,
  isAmenitiesGuideActive,
  isZoomZonesGuideActive,
  zoomZones,
  svgData,
  onMarkerClick,
  isPreviewMode,
} ) => {
  const map = useMapInteractionStore( ( state ) => state.map );
  const overlayBounds = useMapInteractionStore( ( state ) => state.overlayBounds );
  const setActiveLevelOfCareUuid = useMapInteractionStore(
    ( state ) => state.setActiveLevelOfCareUuid,
  );
  const svgFocusId = useMapStateStore( ( state ) => state.svgFocusId );
  const setActiveLevelSvgId = useMapStateStore( ( state ) => state.setActiveLevelSvgId );
  const initialLevelSvgId = useMapStateStore( ( state ) => state.initialLevelSvgId );
  const lastClusterRevealRef = useRef( null );
  const zoomZoneLevelSwitchTimeoutRef = useRef( null );

  useEffect( () => () => {
    if ( zoomZoneLevelSwitchTimeoutRef.current ) {
      clearTimeout( zoomZoneLevelSwitchTimeoutRef.current );
    }
  }, [] );

  const [clusters, setClusters] = useState( [] );
  const [unclusteredPlaces, setUnclusteredPlaces] = useState( [] );
  const [debugBounds, setDebugBounds] = useState( null );
  const [debugClusterBounds, setDebugClusterBounds] = useState( null );

  // Recalculate clusters when zoom / container / places change
  useEffect( () => {
    if ( !places || places.length === 0 ) {
      setClusters( [] );
      setUnclusteredPlaces( [] );
      return;
    }

    // Wait for valid container dimensions before clustering
    if ( !containerSize.width || !containerSize.height ) {
      setClusters( [] );
      setUnclusteredPlaces( places );
      setDebugBounds( null );
      setDebugClusterBounds( null );
      return;
    }

    const timeoutId = setTimeout( () => {
      const linkPlaces = places.filter( ( p ) => p?.pin?.create_modal );
      const nonLinkPlaces = places.filter( ( p ) => !p?.pin?.create_modal );

      const actualMaxZoom = effectiveMaxZoom ?? maxZoom;
      const isAtMaxZoom = actualMaxZoom && activeZoom >= actualMaxZoom;

      if ( !platformClusterPlaceLabels || isAtMaxZoom ) {
        setClusters( [] );
        setUnclusteredPlaces( places );
        return;
      }

      const zoomScale = getZoomScale( activeZoom, mapZoomToEnableLevels );

      // Non-link ("lower value") labels participate in clustering only when
      // they're actually shown on the map. Otherwise they have no visible
      // footprint to collide with and should stay out of the cluster input.
      const placesToCluster = platformShowLowerValuePlaceLabelsInitially
        ? places
        : linkPlaces;
      const placesAlwaysUnclustered = platformShowLowerValuePlaceLabelsInitially
        ? []
        : nonLinkPlaces;

      const {
        clusters: newClusters,
        unclustered,
        debugBounds: newDebugBounds,
        debugClusterBounds: newDebugClusterBounds,
      } = clusterOverlappingLabels( placesToCluster, clusterViewBox, zoomScale, false, containerSize );

      const allUnclustered = [...placesAlwaysUnclustered, ...unclustered];
      setClusters( newClusters );
      setUnclusteredPlaces( allUnclustered );
      setDebugBounds( newDebugBounds );
      setDebugClusterBounds( newDebugClusterBounds );
    }, 150 );

    return () => clearTimeout( timeoutId );
  }, [places, platformClusterPlaceLabels, platformShowLowerValuePlaceLabelsInitially,
    activeZoom, maxZoom, effectiveMaxZoom, clusterViewBox, mapZoomToEnableLevels,
    containerSize] );

  // Reset reveal tracking when the focused amenity changes so a new
  // selection can re-trigger the cluster reveal flow.
  useEffect( () => {
    lastClusterRevealRef.current = null;
  }, [svgFocusId] );

  // If the focused amenity is currently hidden inside a cluster badge,
  // zoom in toward the label's true center until clustering breaks apart
  // and the individual label is revealed. Each zoom step recomputes
  // clusters and re-fires this effect, so the reveal happens iteratively.
  useEffect( () => {
    if ( !svgFocusId || !map || !overlayBounds || !svgData ) return;
    if ( !clusters || clusters.length === 0 ) return;

    const actualMaxZoom = effectiveMaxZoom ?? maxZoom;
    const currentZoom = map.getZoom();
    if ( actualMaxZoom && currentZoom >= actualMaxZoom ) return;

    let labelCenter = null;
    for ( let i = 0; i < clusters.length; i += 1 ) {
      const cluster = clusters[i];
      const idx = cluster.labels.findIndex( ( p ) => p?.pin?.svg_id === svgFocusId );
      if ( idx !== -1 ) {
        labelCenter = cluster.svgCenters[idx] || cluster.center;
        break;
      }
    }
    if ( !labelCenter ) return;

    // Avoid re-issuing the same zoom for the same focus while the
    // animation is still in flight (clusters may re-render mid-anim).
    const revealKey = `${svgFocusId}:${currentZoom}`;
    if ( lastClusterRevealRef.current === revealKey ) return;
    lastClusterRevealRef.current = revealKey;

    const nextZoom = actualMaxZoom
      ? Math.min( currentZoom + 1, actualMaxZoom )
      : currentZoom + 1;
    const focusXYZ = { x: labelCenter.x, y: labelCenter.y, z: nextZoom };
    const targetLatLng = getLatLngFromFocusXYZ( focusXYZ, svgData, overlayBounds );
    animateMapNavigation( map, targetLatLng, nextZoom );
  }, [svgFocusId, clusters, map, overlayBounds, svgData, effectiveMaxZoom, maxZoom] );

  const handleClusterClick = useCallback( ( cluster ) => {
    if ( !map || !overlayBounds || !svgData || !cluster.labels || cluster.labels.length === 0 ) return;

    // Compute centroid of cluster member places
    const centers = cluster.labels
      .map( ( place ) => getCenterFromPlace( place ) )
      .filter( Boolean );
    if ( centers.length === 0 ) return;

    const centroidX = centers.reduce( ( sum, c ) => sum + c.x, 0 ) / centers.length;
    const centroidY = centers.reduce( ( sum, c ) => sum + c.y, 0 ) / centers.length;

    const currentZoom = map.getZoom();
    const nextZoom = currentZoom + 1;
    const focusXYZ = { x: centroidX, y: centroidY, z: nextZoom };
    const centroidLatLng = getLatLngFromFocusXYZ( focusXYZ, svgData, overlayBounds );
    animateMapNavigation( map, centroidLatLng, nextZoom );
  }, [map, overlayBounds, svgData] );

  // Per-zone footprint bbox in SVG coords. Built from the centers of every
  // place and unit on the current floor tagged with the zone's
  // map_building_grouping uuid. Used by MapLabels to constrain the zone-icon
  // ring-search shift to the "building" area when a zoom-zone label can't
  // find a clean orientation at its base position and no in-zone cluster
  // qualifies for absorption. Bbox of centers (not shape outlines) — close
  // enough as an approximation; could later be replaced by a Drupal-computed
  // shape footprint exposed on each zoom_zone.
  const zoneFootprints = useMemo( () => {
    const result = new Map();
    const accumulate = ( uuid, x, y ) => {
      if ( !uuid ) return;
      if ( x == null || y == null || Number.isNaN( x ) || Number.isNaN( y ) ) return;
      const existing = result.get( uuid );
      if ( existing ) {
        existing.minX = Math.min( existing.minX, x );
        existing.minY = Math.min( existing.minY, y );
        existing.maxX = Math.max( existing.maxX, x );
        existing.maxY = Math.max( existing.maxY, y );
      } else {
        result.set( uuid, {
          minX: x, minY: y, maxX: x, maxY: y,
        } );
      }
    };
    places?.forEach( ( p ) => {
      const center = getCenterFromPlace( p );
      if ( !center ) return;
      accumulate( p?.map_building_grouping?.uuid, center.x, center.y );
    } );
    units?.forEach( ( u ) => {
      const lbl = u?.label;
      const x = lbl?.x !== undefined ? parseFloat( lbl.x ) : null;
      const y = lbl?.y !== undefined ? parseFloat( lbl.y ) : null;
      accumulate( u?.info?.map_building_grouping?.uuid, x, y );
    } );
    return result;
  }, [places, units] );

  const handleZoomZoneClick = useCallback( ( zoomZone ) => {
    if ( map && overlayBounds && svgData && zoomZone.center ) {
      // From bird's-eye, zoom into the floor band (mapZoomToEnableLevels) and
      // defer the level switch so it happens after the zoom animation passes
      // the level-enable threshold. From a floor, the user is already past
      // that threshold — pan to the zone center at the current zoom and
      // switch the level immediately so the click feels responsive.
      const currentZoom = map.getZoom();
      const targetZoom = isBirdsEye
        ? ( mapZoomToEnableLevels || currentZoom + 1 )
        : currentZoom;
      const focusXYZ = { x: zoomZone.center.x, y: zoomZone.center.y, z: targetZoom };
      const zoneLatLng = getLatLngFromFocusXYZ( focusXYZ, svgData, overlayBounds );
      animateMapNavigation( map, zoneLatLng, targetZoom );

      const primaryLocUuid = pickPrimaryLevelOfCareUuid( zoomZone.level_of_care )
        || defaultLevelOfCare;
      if ( primaryLocUuid ) {
        setActiveLevelOfCareUuid( primaryLocUuid );
      }

      const targetLevelSvgId = pickZoomZoneTargetLevelSvgId( zoomZone, initialLevelSvgId );
      if ( targetLevelSvgId ) {
        if ( zoomZoneLevelSwitchTimeoutRef.current ) {
          clearTimeout( zoomZoneLevelSwitchTimeoutRef.current );
        }
        if ( isBirdsEye ) {
          zoomZoneLevelSwitchTimeoutRef.current = setTimeout( () => {
            setActiveLevelSvgId( targetLevelSvgId );
            zoomZoneLevelSwitchTimeoutRef.current = null;
          }, ZOOM_ZONE_LEVEL_SWITCH_DELAY );
        } else {
          setActiveLevelSvgId( targetLevelSvgId );
        }
      }
    }
  }, [map, overlayBounds, svgData, mapZoomToEnableLevels, isBirdsEye,
    defaultLevelOfCare, initialLevelSvgId,
    setActiveLevelSvgId, setActiveLevelOfCareUuid] );

  const handleOverflowClusterClick = useCallback((coveredUuids) => {
    if (!map || !overlayBounds || !svgData || !Array.isArray(coveredUuids)) return;
    // Compute centroid of covered places
    const centers = coveredUuids
      .map((uuid) => places.find((p) => p.uuid === uuid || p.pin?.uuid === uuid))
      .filter(Boolean)
      .map((place) => getCenterFromPlace(place))
      .filter(Boolean);
    if (centers.length === 0) return;
    const centroid = {
      x: centers.reduce((sum, c) => sum + c.x, 0) / centers.length,
      y: centers.reduce((sum, c) => sum + c.y, 0) / centers.length,
    };
    const currentZoom = map.getZoom();
    const nextZoom = currentZoom + 1;
    const focusXYZ = { x: centroid.x, y: centroid.y, z: nextZoom };
    const centroidLatLng = getLatLngFromFocusXYZ(focusXYZ, svgData, overlayBounds);
    animateMapNavigation(map, centroidLatLng, nextZoom);
  }, [map, overlayBounds, svgData, places]);

  const hasFloorZoomZones = !isBirdsEye
    && platformShowZoomZonesOnMapLevels
    && floorZoomZones
    && Object.keys(floorZoomZones).length > 0;

  return (
    <>
      {!isBirdsEye && parsedViewBox && (
        <MapLabels
          places={unclusteredPlaces}
          clusters={clusters}
          units={units}
          unitsVersion={unitsVersion}
          unclusteredPlaces={unclusteredPlaces}
          clusteringEnabled={platformClusterPlaceLabels}
          zoomZones={{}}
          showZoomZones={false}
          showUnitLabels
          showPlaceLabels={markersVisible}
          platformShowOccupancy={platformShowOccupancy}
          platformShowMapPlaceLabelsInitially={platformShowMapPlaceLabelsInitially}
          platformShowLowerValuePlaceLabelsInitially={platformShowLowerValuePlaceLabelsInitially}
          isAmenitiesGuideActive={isAmenitiesGuideActive}
          viewBox={parsedViewBox}
          activeZoom={activeZoom}
          baseZoom={mapZoomToEnableLevels || 18}
          zIndex={5}
          onMarkerClick={onMarkerClick}
          onClusterClick={handleClusterClick}
          onZoomZoneClick={handleZoomZoneClick}
          onOverflowClusterClick={handleOverflowClusterClick}
          debugBounds={debugBounds}
          debugClusterBounds={debugClusterBounds}
          isPreviewMode={isPreviewMode}
        />
      )}

      {isBirdsEye && markersVisible && activeZoom >= zoomZonesActivationLevel && parsedViewBox && (
        <MapLabels
          places={[]}
          clusters={clusters}
          unclusteredPlaces={[]}
          clusteringEnabled={false}
          zoomZones={zoomZones}
          zoneFootprints={zoneFootprints}
          showZoomZones
          isZoomZonesGuideActive={isZoomZonesGuideActive}
          viewBox={parsedViewBox}
          zIndex={10}
          onMarkerClick={onMarkerClick}
          onClusterClick={handleClusterClick}
          onZoomZoneClick={handleZoomZoneClick}
          onOverflowClusterClick={handleOverflowClusterClick}
        />
      )}

      {hasFloorZoomZones && markersVisible && parsedViewBox && (
        <MapLabels
          places={[]}
          clusters={clusters}
          unclusteredPlaces={[]}
          clusteringEnabled={false}
          zoomZones={floorZoomZones}
          zoneFootprints={zoneFootprints}
          showZoomZones
          zoomZonesOnFloor
          isZoomZonesGuideActive={isZoomZonesGuideActive}
          viewBox={parsedViewBox}
          zIndex={10}
          onMarkerClick={onMarkerClick}
          onClusterClick={handleClusterClick}
          onZoomZoneClick={handleZoomZoneClick}
          onOverflowClusterClick={handleOverflowClusterClick}
        />
      )}
    </>
  );
};

export default MapOverlayLabels;
